import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'

function formatCurrency(value) {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `$${Math.round(value / 1000)}K`
  return `$${value}`
}

function ExceedanceCurveChart({ exceedanceCurve, percentiles, coverageLimit, recommendedCoverage, recommendedCoverageRationale, executiveSummary }) {
  const rawData = exceedanceCurve?.map((point) => ({
    threshold: point.threshold,
    probability: Math.round(point.probability * 100 * 10) / 10,
  })) || []

  const data = [...rawData]

  // Percentile values for the summary
  const p50 = percentiles?.p50 || 0
  const p90 = percentiles?.p90 || 0
  const p95 = percentiles?.p95 || 0
  const p99 = percentiles?.p99 || 0

  // Find probability at coverage limit by interpolation
  const probAtCoverage = (() => {
    if (!coverageLimit || rawData.length === 0) return null
    for (let i = 0; i < rawData.length - 1; i++) {
      if (rawData[i].threshold <= coverageLimit && rawData[i + 1].threshold >= coverageLimit) {
        const ratio = (coverageLimit - rawData[i].threshold) / (rawData[i + 1].threshold - rawData[i].threshold)
        return Math.round((rawData[i].probability - (rawData[i].probability - rawData[i + 1].probability) * ratio) * 10) / 10
      }
    }
    return rawData[rawData.length - 1]?.probability || 0
  })()

  const pctExceedsCoverage = probAtCoverage != null ? Math.round(probAtCoverage) : null

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null
    const item = payload[0].payload
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 shadow-lg text-xs">
        <p className="text-gray-900 dark:text-white font-medium">{formatCurrency(item.threshold)}</p>
        <p className="text-gray-500 dark:text-gray-400">Probability: {item.probability}%</p>
      </div>
    )
  }

  // Coverage limit label — positioned above the line
  const renderCoverageLimitLabel = (props) => {
    const { viewBox } = props
    if (!viewBox) return null
    return (
      <g>
        <rect x={viewBox.x - 44} y={viewBox.y + 8} width={88} height={16} rx={3} fill="#F59E0B" opacity={0.15} />
        <text x={viewBox.x} y={viewBox.y + 19} textAnchor="middle" fill="#F59E0B" fontSize={9} fontWeight={600}>
          Current ({formatCurrency(coverageLimit)})
        </text>
      </g>
    )
  }

  return (
    <div className="relative">
      {/* Legend — top right corner */}
      <div className="absolute top-8 right-12 z-10 bg-gray-50/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-lg border border-gray-200 dark:border-gray-700/40 shadow-sm px-3 py-2 space-y-1">
        <div className="flex items-center gap-2">
          <div className="w-3 h-0.5 bg-[#2EC4B6] rounded" />
          <span className="text-[10px] text-gray-600 dark:text-gray-300 font-medium">Exceedance probability</span>
        </div>
        {coverageLimit > 0 && (
          <div className="flex items-center gap-2">
            <div className="w-3 h-0.5 border-t border-dashed border-[#F59E0B]" />
            <span className="text-[10px] font-medium" style={{ color: '#F59E0B' }}>
              Current coverage ({formatCurrency(coverageLimit)})
            </span>
          </div>
        )}
        {recommendedCoverage > 0 && recommendedCoverage !== coverageLimit && (
          <div className="flex items-center gap-2">
            <div className="w-3 h-0.5 border-t border-dashed border-[#22C55E]" />
            <span className="text-[10px] font-medium" style={{ color: '#22C55E' }}>
              Recommended ({formatCurrency(recommendedCoverage)})
            </span>
          </div>
        )}
        {((p90 > 0 && p90 !== recommendedCoverage && p90 !== coverageLimit) ||
          (p99 > 0 && p99 !== recommendedCoverage && p99 !== coverageLimit)) && (
          <div className="flex items-center gap-2">
            <div className="w-3 h-px border-t border-dashed border-[#9CA3AF]" />
            <span className="text-[10px] text-gray-500 dark:text-gray-400">P90 / P99 reference</span>
          </div>
        )}
        {coverageLimit > 0 && pctExceedsCoverage != null && (
          <div className="pt-1 mt-1 border-t border-gray-100 dark:border-gray-700/30">
            <span className="text-[10px] text-gray-500 dark:text-gray-400">
              <span className="font-medium text-red-500">{pctExceedsCoverage}%</span> exceed coverage
            </span>
          </div>
        )}
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={data} margin={{ top: 20, right: 40, left: 10, bottom: 5 }}>
          <defs>
            <linearGradient id="exceedanceGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2EC4B6" stopOpacity={0.2} />
              <stop offset="100%" stopColor="#2EC4B6" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" opacity={0.5} />
          <XAxis
            dataKey="threshold"
            tickFormatter={formatCurrency}
            tick={{ fontSize: 11, fill: '#9CA3AF' }}
            axisLine={{ stroke: '#D1D5DB' }}
            tickLine={{ stroke: '#D1D5DB' }}
            type="number"
          />
          <YAxis
            domain={[0, 100]}
            tickFormatter={(v) => `${v}%`}
            tick={{ fontSize: 11, fill: '#9CA3AF' }}
            axisLine={{ stroke: '#D1D5DB' }}
            tickLine={{ stroke: '#D1D5DB' }}
            width={45}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="probability"
            stroke="#2EC4B6"
            strokeWidth={2}
            fill="url(#exceedanceGradient)"
            dot={false}
            activeDot={{ r: 4, fill: '#2EC4B6', stroke: '#fff', strokeWidth: 2 }}
          />
          {coverageLimit > 0 && (
            <ReferenceLine
              x={coverageLimit}
              stroke="#F59E0B"
              strokeDasharray="6 3"
              strokeWidth={2}
              label={renderCoverageLimitLabel}
            />
          )}
          {p90 > 0 && p90 !== recommendedCoverage && p90 !== coverageLimit && (
            <ReferenceLine
              x={p90}
              stroke="#9CA3AF"
              strokeDasharray="2 4"
              strokeWidth={1}
              label={(props) => {
                const { viewBox } = props
                if (!viewBox) return null
                return (
                  <text x={viewBox.x} y={viewBox.y - 4} textAnchor="middle" fill="#9CA3AF" fontSize={9}>P90</text>
                )
              }}
            />
          )}
          {p99 > 0 && p99 !== recommendedCoverage && p99 !== coverageLimit && (
            <ReferenceLine
              x={p99}
              stroke="#9CA3AF"
              strokeDasharray="2 4"
              strokeWidth={1}
              label={(props) => {
                const { viewBox } = props
                if (!viewBox) return null
                return (
                  <text x={viewBox.x} y={viewBox.y - 4} textAnchor="middle" fill="#9CA3AF" fontSize={9}>P99</text>
                )
              }}
            />
          )}
          {recommendedCoverage > 0 && recommendedCoverage !== coverageLimit && (
            <ReferenceLine
              x={recommendedCoverage}
              stroke="#22C55E"
              strokeDasharray="6 3"
              strokeWidth={2}
              label={(props) => {
                const { viewBox } = props
                if (!viewBox) return null
                return (
                  <g>
                    <rect x={viewBox.x - 48} y={viewBox.y + 28} width={96} height={16} rx={3} fill="#22C55E" opacity={0.15} />
                    <text x={viewBox.x} y={viewBox.y + 39} textAnchor="middle" fill="#22C55E" fontSize={9} fontWeight={600}>
                      Recommended ({formatCurrency(recommendedCoverage)})
                    </text>
                  </g>
                )
              }}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>

      {executiveSummary && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 leading-relaxed">
          {executiveSummary}
        </p>
      )}

      {coverageLimit > 0 && (
        <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2 italic">
          Insurance estimates are for planning purposes only. Actual coverage is subject to policy limitations, exclusions, sub-limits, and waiting periods. Review all coverage details with your carrier or a qualified insurance professional.
        </p>
      )}
    </div>
  )
}

export default ExceedanceCurveChart
